import { useQueryClient } from "@tanstack/react-query";
import { Link2 } from "lucide-react";

import {
  linkPermitApplicationDocument,
  listPermitApplicationDocuments,
  PERMIT_APPLICATION_DOCUMENT_ROLE_LABELS,
  unlinkPermitApplicationDocument,
  type PermitApplicationDocumentRole,
} from "@/api/permit_application_documents";
import { PermitApplicationSection } from "@/components/permit_suite/PermitApplicationSection";
import {
  useWorkflowDocumentLinks,
  WORKFLOW_DOCUMENT_LINK_HELPER_TEXT,
  WorkflowDocumentLinkButton,
  WorkflowDocumentLinkList,
  WorkflowDocumentLinkPickerDialog,
} from "@/components/workflow_tracking/WorkflowDocumentLinks";
import { invalidatePermitSuitePipeline } from "@/lib/permitSuiteQueryKeys";

type PermitApplicationDocumentsProps = {
  municipalityId: number;
  applicationId: number;
  embedded?: boolean;
};

const PERMIT_ROLE_OPTIONS = Object.entries(PERMIT_APPLICATION_DOCUMENT_ROLE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

export function PermitApplicationDocuments({
  municipalityId,
  applicationId,
  embedded = false,
}: PermitApplicationDocumentsProps) {
  const queryClient = useQueryClient();
  const linksKey = ["permit_application_documents", municipalityId, applicationId] as const;

  const links = useWorkflowDocumentLinks({
    municipalityId,
    linksQueryKey: linksKey,
    listLinks: () => listPermitApplicationDocuments(municipalityId, applicationId),
    linkDocument: (documentId, role) =>
      linkPermitApplicationDocument(municipalityId, applicationId, documentId, {
        role: role as PermitApplicationDocumentRole,
      }),
    unlinkDocument: (linkId) =>
      unlinkPermitApplicationDocument(municipalityId, applicationId, linkId),
    defaultRole: "attachment",
    onInvalidate: () => invalidatePermitSuitePipeline(queryClient, municipalityId),
  });

  const picker = (
    <WorkflowDocumentLinkPickerDialog
      open={links.pickerOpen}
      onOpenChange={links.setPickerOpen}
      description="Choose a library document to attach to this permit application."
      selectedDocumentId={links.selectedDocumentId}
      onSelectedDocumentIdChange={links.setSelectedDocumentId}
      selectedRole={links.selectedRole}
      onSelectedRoleChange={links.setSelectedRole}
      roleOptions={PERMIT_ROLE_OPTIONS}
      documentSearch={links.documentSearch}
      onDocumentSearchChange={links.setDocumentSearch}
      searchResults={links.searchResults}
      searchLoading={links.searchLoading}
      searchReady={links.searchReady}
      linking={links.linkMutation.isPending}
      onLink={() =>
        links.linkMutation.mutate({
          documentId: Number(links.selectedDocumentId),
          role: links.selectedRole,
        })
      }
    />
  );

  const list = (
    <WorkflowDocumentLinkList
      links={links.links}
      isLoading={links.isLoading}
      error={links.error}
      roleLabels={PERMIT_APPLICATION_DOCUMENT_ROLE_LABELS}
      emptyMessage="No documents linked yet. Attach plans, applications, and supporting files from File Center."
      unlinking={links.unlinkMutation.isPending}
      onUnlink={(linkId) => links.unlinkMutation.mutate(linkId)}
    />
  );

  if (embedded) {
    return (
      <div className="space-y-4" data-testid="permit-application-documents">
        <div className="flex items-center gap-2">
          <p className="min-w-0 flex-1 text-sm text-muted-foreground">
            {WORKFLOW_DOCUMENT_LINK_HELPER_TEXT}
          </p>
          <WorkflowDocumentLinkButton className="shrink-0" onClick={links.openPicker} />
        </div>
        {list}
        {picker}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="permit-application-documents">
      <PermitApplicationSection
        title="Documents"
        description={WORKFLOW_DOCUMENT_LINK_HELPER_TEXT}
        action={
          links.links.length > 0 ? (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Link2 className="h-3.5 w-3.5" />
                {links.links.length} linked
              </span>
              <WorkflowDocumentLinkButton onClick={links.openPicker} />
            </div>
          ) : (
            <WorkflowDocumentLinkButton onClick={links.openPicker} />
          )
        }
      >
        {list}
        {picker}
      </PermitApplicationSection>
    </div>
  );
}
